import type { SortByDirection } from '@paid-ui/types/sort';

import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { ArrowDownAZIcon, ArrowDownIcon, ArrowUpAZIcon, ArrowUpIcon } from 'lucide-react';

import { SearchInput } from '@paid-ui/components/search-input';
import { TableSortControl } from '@paid-ui/components/table-new';
import { parseSortDirection } from '@paid-ui/types/sort';

import { useApplyFilters } from './use-apply-filter';

interface Props {
  disabled?: boolean;
  /** Total number of projects matching the current filters. */
  total?: number;
}

const sortOptions = [
  {
    label: 'Created date (Default)',
    value: 'CREATED_AT',
    icon: ArrowDownIcon,
    ascIcon: ArrowUpIcon,
    descIcon: ArrowDownIcon,
  },
  {
    label: 'Project name',
    value: 'PROJECT_NAME',
    icon: ArrowDownAZIcon,
    ascIcon: ArrowUpAZIcon,
    descIcon: ArrowDownAZIcon,
  },
];

export const CardsToolbar: React.FC<Props> = ({ disabled, total }) => {
  const searchParams = useSearchParams();
  const applyFilters = useApplyFilters();

  const keywordQuery = searchParams.get('searchTerm') ?? '';
  const sortByKey = searchParams.get('sortByKey') ?? 'CREATED_AT';
  const sortByDirection = parseSortDirection(searchParams.get('sortByDirection'));

  const [keyword, setKeyword] = useState(keywordQuery);

  // Mirror the URL back into the input, so Back and a cleared filter both land.
  useEffect(() => {
    setKeyword(keywordQuery);
  }, [keywordQuery]);

  const handleSearchTermChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(event.currentTarget.value);
  };

  const handleSearchTermBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    applyFilters({ searchTerm: event.currentTarget.value });
  };

  const handleSearchTermKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.code === 'Enter') {
      applyFilters({ searchTerm: event.currentTarget.value });
    }
  };

  const handleSortChange = (key: string, direction: SortByDirection) => {
    applyFilters({
      sortByKey: key === 'CREATED_AT' ? '' : key,
      sortByDirection: direction === 'DESC' ? '' : direction,
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-2.5">
      <SearchInput
        compact
        disabled={disabled}
        value={keyword}
        name="searchTerm"
        aria-label="Search projects"
        onChange={handleSearchTermChange}
        onKeyDown={handleSearchTermKeyDown}
        onBlur={handleSearchTermBlur}
        placeholder="Search by number, name or address"
        className="w-full sm:w-80"
      />
      <TableSortControl
        options={sortOptions}
        defaultSortKey="CREATED_AT"
        value={sortByKey}
        direction={sortByDirection}
        onChange={handleSortChange}
      />
      {typeof total === 'number' ? (
        <p className="ml-auto text-sm text-grey-dark">
          {total === 1 ? '1 project' : `${total} projects`}
        </p>
      ) : null}
    </div>
  );
};
